import { Checkbox } from 'antd';
import type { ColumnGroupType } from 'antd/lib/table';
import React, { useEffect, useMemo, useState } from 'react';

import { COLORS } from '@src/emp-3.0/utils/constants';
import { parseNumToPercentage } from '@src/emp-3.0/utils/number';

import { FunnelTableWrapper } from './styled';
import { isGeoDimension } from '@src/emp-3.0/utils';

const STEPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
interface ConversionMetrics {
  conversion_from_previous: number;
  conversion_from_first: number;
  count: number;
}
type DataType = {
  total_conversion: number;
  selected: boolean;
  [key: string]: any;
} & Record<string, ConversionMetrics>;

const FunnelTable = ({ style, data, options, onChecked, breakdowns = [], disableCheckbox }) => {
  // render a bar chart base on funneldData
  const { funnel_data, event_names } = data;
  const [selected, setSelected] = useState<string[]>([]);

  const nowBreakdown = useMemo(() => {
    const dimension = breakdowns?.[0]?.[0]?.field;
    return isGeoDimension(dimension) ? `${dimension}_code` : dimension;
  }, [breakdowns]);

  const dataSource = useMemo(
    () =>
      funnel_data?.map((data, index, arr) => {
        // The first two items are selected by default.
        const selected = arr.length > 1 ? index > 0 && index < 3 : true;

        const obj = {
          selected,
          ...data,
        } as DataType;
        // transform data to step1.xxx
        STEPS.forEach((i) => {
          const step = data[`step_${i}`];
          if (step) {
            (Object.keys(step) as Array<keyof ConversionMetrics>).forEach((key) => {
              obj[`step_${i}.${key}`] = step[key];
            });
          }
        });
        return obj;
      }) || [],
    [funnel_data],
  );

  useEffect(() => {
    const initSelected = dataSource.reduce((acc, v) => {
      if (v.selected && v[nowBreakdown] !== undefined) {
        acc.push(v[nowBreakdown]);
      }
      return acc;
    }, []);
    setSelected(initSelected);
    initSelected && onChecked(initSelected);
  }, [dataSource, nowBreakdown]);

  const onCheck = (e, value) => {
    const checked = e.target.checked;
    const selectedNew = checked ? [...selected, value] : selected.filter((v) => v !== value);
    setSelected(selectedNew);
    onChecked(selectedNew);
  };
  const renderFirstColumn = (_, record, index) => {
    if (index === 0) return <div></div>;
    if (!breakdowns.length || !breakdowns[0]?.length) return <div></div>;
    const breakDown = record[nowBreakdown];
    return (
      <div className="checkbox-wrapper" style={{ backgroundColor: COLORS[index - 1] }}>
        <Checkbox
          checked={selected.includes(breakDown)}
          onChange={(e) => {
            onCheck(e, breakDown);
          }}
          disabled={disableCheckbox}
        />
      </div>
    );
  };

  const renderStepTitle = (title) => {
    return <div className="left-wrapper">{title}</div>;
  };

  const generateSteps = () => {
    return (
      event_names?.map((event, i) => {
        const index = i + 1;
        if (index === 1) {
          return {
            title: renderStepTitle(`Step 1: ${event}`),
            className: 'left-border',
            children: [
              {
                title: 'Sample Count',
                className: 'left-border',
                dataIndex: 'step_1.count',
                key: 'step_1.count',
                render: (value) => {
                  return <div className="left-wrapper">{value}</div>;
                },
              },
            ],
          };
        }
        return {
          title: renderStepTitle(`Step ${index}: ${event}`),
          className: 'left-border',
          children: [
            {
              title: 'Conv. from Previous',
              className: 'left-border',
              dataIndex: `step_${index}.conversion_from_previous`,
              key: `step_${index}.conversion_from_previous`,
              render: (value) => {
                return <div className="left-wrapper">{parseNumToPercentage(value, 2)}</div>;
              },
            },
            {
              title: 'Conv. from First',
              dataIndex: `step_${index}.conversion_from_first`,
              key: `step_${index}.conversion_from_first`,
              render: (value) => {
                return <div className="left-wrapper">{parseNumToPercentage(value, 2)}</div>;
              },
            },
            {
              title: 'Sample Count',
              dataIndex: `step_${index}.count`,
              key: `step_${index}.count`,
              render: (value) => {
                return <div>{value}</div>;
              },
            },
          ],
        };
      }) || []
    );
  };

  const generateColumns = () => {
    return breakdowns?.map((breakdown, i) => {
      return {
        title: <div></div>,
        children: [
          {
            title:
              options?.find((option) => option.value === breakdown[i].field)?.label ||
              breakdown[i].field,
            dataIndex: `${breakdown[i].field}`,
            key: `breakdown_${i}.count`,
            render: (value, _, index) => {
              if (!value && index === 0) {
                return <div className="align-right">Overall</div>;
              }
              return <div>{value}</div>;
            },
          },
        ],
      };
    });
  };

  const columns: ColumnGroupType<object>[] = [
    {
      title: <div></div>,
      className: 'checkbox-column',
      children: [
        {
          title: '',
          dataIndex: 'selected',
          key: 'selected',
          render: renderFirstColumn,
        },
      ],
    },
    ...generateColumns(),
    {
      title: <div></div>,
      children: [
        {
          title: 'Total Conv.',
          dataIndex: 'total_conversion',
          key: 'total_conversion',
          render: (value) => {
            return <div>{parseNumToPercentage(value, 2)}</div>;
          },
        },
      ],
    },
    ...generateSteps(),
  ];

  return (
    <FunnelTableWrapper
      style={{ ...style }}
      pagination={false}
      rowClassName={(record, index) => {
        if (index === 0) {
          return 'first-row';
        }
        return '';
      }}
      dataSource={dataSource}
      columns={columns}
    />
  );
};
export default FunnelTable;
